import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import './Auth.css'

// ─────────────────────────────────────────────────────────────────────────────
// Data
// ─────────────────────────────────────────────────────────────────────────────

const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh','Belgium',
  'Brazil','Canada','Chile','China','Colombia','Croatia','Czech Republic','Denmark','Egypt',
  'Ethiopia','Finland','France','Germany','Ghana','Greece','Hungary','India','Indonesia',
  'Iran','Iraq','Ireland','Israel','Italy','Japan','Jordan','Kenya','Malaysia','Mexico',
  'Morocco','Netherlands','New Zealand','Nigeria','Norway','Pakistan','Peru','Philippines',
  'Poland','Portugal','Romania','Russia','Saudi Arabia','Senegal','Serbia','Singapore',
  'South Africa','South Korea','Spain','Sri Lanka','Sweden','Switzerland','Taiwan','Tanzania',
  'Thailand','Tunisia','Turkey','Uganda','Ukraine','United Arab Emirates','United Kingdom',
  'United States','Vietnam','Other',
]

const LANGUAGES = [
  'Arabic','Bengali','Chinese (Cantonese)','Chinese (Mandarin)','Czech','Danish','Dutch',
  'English','Farsi','Filipino','Finnish','French','German','Greek','Hebrew','Hindi',
  'Hungarian','Indonesian','Italian','Japanese','Korean','Malay','Norwegian','Polish',
  'Portuguese','Romanian','Russian','Serbian','Spanish','Swahili','Swedish','Tamil',
  'Thai','Turkish','Ukrainian','Urdu','Vietnamese','Other',
]

const SITUATIONS = [
  { value: 'student',          label: 'Student — currently studying' },
  { value: 'career_changer',   label: 'Career changer — moving into IT' },
  { value: 'employed_outside', label: 'Employed — outside of IT' },
  { value: 'early_it',         label: 'Working in IT — early in my career' },
  { value: 'other',            label: 'Other' },
]

const LOGO = (
  <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="#0a0c10" strokeWidth="1.5"/>
    <path d="M5 8h6M5 10.5h3" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M6 2h4" stroke="#0a0c10" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
)

function isDuplicateEmailError(msg) {
  const m = (msg || '').toLowerCase()
  return m.includes('already registered') || m.includes('already exists') ||
         m.includes('user already')       || m.includes('email already')
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab switcher
// ─────────────────────────────────────────────────────────────────────────────

function Tabs({ active, onChange }) {
  return (
    <div className="auth-tabs">
      <button
        className={`auth-tab${active === 'signin' ? ' auth-tab--active' : ''}`}
        onClick={() => onChange('signin')}
        type="button"
      >
        Sign in
      </button>
      <button
        className={`auth-tab${active === 'register' ? ' auth-tab--active' : ''}`}
        onClick={() => onChange('register')}
        type="button"
      >
        Create account
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sign in panel
// ─────────────────────────────────────────────────────────────────────────────

function SignInPanel({ onSwitch }) {
  const { signIn } = useAuth()

  const [form, setForm]       = useState({ email: '', password: '' })
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  // NOTE: redirect-on-authenticated is handled by the parent Login component.
  // Having a second redirect effect here caused a race where both effects
  // fired against the same router state, intermittently bouncing the user
  // back to /login after a successful sign-in.

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))
    setError('')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.email || !form.password) {
      setError('Please enter your email and password.')
      return
    }
    setLoading(true)
    try {
      const { error: signInErr } = await signIn({ email: form.email, password: form.password })
      if (signInErr) {
        const msg = signInErr.message || ''
        if (msg.toLowerCase().includes('email not confirmed')) {
          setError('Please confirm your email address before signing in. Check your inbox.')
        } else {
          setError('Email or password is incorrect.')
        }
        setLoading(false)
        return
      }
      // signIn succeeded — parent Login component's useEffect redirects on isAuthenticated
    } catch (e) {
      console.error('Login: signIn threw:', e)
      setError('Network error. Check your connection and try again.')
      setLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">Sign in to continue your progress.</p>
      </div>

      {error && <div className="auth-error" role="alert">{error}</div>}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <Input
          id="email" name="email" type="email" label="Email"
          value={form.email} onChange={handleChange}
          required autoFocus autoComplete="email"
        />
        <Input
          id="password" name="password" type="password" label="Password"
          value={form.password} onChange={handleChange}
          required autoComplete="current-password"
        />
        <Button type="submit" fullWidth loading={loading} size="lg">Sign in</Button>
        <p style={{ textAlign: 'right', marginTop: -8 }}>
          <Link to="/forgot-password" style={{ fontSize: '0.78rem', color: 'var(--text-3)' }}>
            Forgot password?
          </Link>
        </p>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Register panel — Step 1
// ─────────────────────────────────────────────────────────────────────────────

function RegisterStep1({ onNext }) {
  const [form, setForm] = useState({
    fullName: '', email: '', password: '', confirm: '',
    country: '', nativeLanguage: '', situation: '', whyHere: '',
    termsAccepted: false,
  })
  const [errors, setErrors] = useState({})

  function handleChange(e) {
    const { name, value, type, checked } = e.target
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    setErrors(prev => ({ ...prev, [name]: '' }))
  }

  function validate() {
    const errs = {}
    if (!form.fullName.trim())          errs.fullName       = 'Required.'
    if (!form.email.trim())             errs.email          = 'Required.'
    if (form.password.length < 8)       errs.password       = 'Minimum 8 characters.'
    if (form.password !== form.confirm) errs.confirm        = 'Passwords do not match.'
    if (!form.country)                  errs.country        = 'Required.'
    if (!form.nativeLanguage)           errs.nativeLanguage = 'Required.'
    if (!form.situation)                errs.situation      = 'Required.'
    return errs
  }

  function handleNext(e) {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setErrors(errs); return }
    onNext({ ...form, termsAcceptedAt: new Date().toISOString(), termsVersion: '2026-05' })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Step 1 of 2 — Your details</p>
      </div>

      <form className="auth-form" onSubmit={handleNext} noValidate>
        <div className="auth-section-label">Account</div>

        <Input id="fullName" name="fullName" type="text" label="Full name"
          value={form.fullName} onChange={handleChange} error={errors.fullName}
          required autoFocus autoComplete="name" />
        <Input id="email" name="email" type="email" label="Email"
          value={form.email} onChange={handleChange} error={errors.email}
          required autoComplete="email" />
        <Input id="password" name="password" type="password" label="Password"
          value={form.password} onChange={handleChange} error={errors.password}
          hint="Minimum 8 characters" required autoComplete="new-password" />
        <Input id="confirm" name="confirm" type="password" label="Confirm password"
          value={form.confirm} onChange={handleChange} error={errors.confirm}
          required autoComplete="new-password" />

        <div className="auth-section-label">About you</div>
        <p className="auth-section-hint">
          Used anonymously for platform improvement — never shared or sold.
        </p>

        <div className={`auth-field${errors.country ? ' auth-field--error' : ''}`}>
          <label className="auth-label" htmlFor="country">Country</label>
          <select id="country" name="country" className="auth-select"
            value={form.country} onChange={handleChange} required>
            <option value="">Select your country</option>
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {errors.country && <span className="auth-error-msg">{errors.country}</span>}
        </div>

        <div className={`auth-field${errors.nativeLanguage ? ' auth-field--error' : ''}`}>
          <label className="auth-label" htmlFor="nativeLanguage">Native language</label>
          <select id="nativeLanguage" name="nativeLanguage" className="auth-select"
            value={form.nativeLanguage} onChange={handleChange} required>
            <option value="">Select your language</option>
            {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          {errors.nativeLanguage && <span className="auth-error-msg">{errors.nativeLanguage}</span>}
        </div>

        <div className={`auth-field${errors.situation ? ' auth-field--error' : ''}`}>
          <label className="auth-label" htmlFor="situation">Current situation</label>
          <select id="situation" name="situation" className="auth-select"
            value={form.situation} onChange={handleChange} required>
            <option value="">Select your situation</option>
            {SITUATIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {errors.situation && <span className="auth-error-msg">{errors.situation}</span>}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="whyHere">
            Why do you want in? <span className="auth-optional">Optional</span>
          </label>
          <textarea id="whyHere" name="whyHere" className="auth-textarea" rows={3}
            placeholder="What are you hoping to get out of TechBench?"
            value={form.whyHere} onChange={handleChange} />
        </div>

        {/* Terms acceptance — explicit checkbox required */}
        <div className="auth-terms-block">
          <label className="auth-terms-label">
            <input
              type="checkbox"
              name="termsAccepted"
              checked={form.termsAccepted}
              onChange={handleChange}
              className="auth-terms-checkbox"
            />
            <span>
              I have read and accept the{' '}
              <a href="https://techbench.it/terms" target="_blank" rel="noreferrer">Terms of Service</a>
              {' '}and{' '}
              <a href="https://techbench.it/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
              I understand that by requesting immediate access to digital content,
              I waive my 14-day right of withdrawal in accordance with Article 16(m)
              of the EU Consumer Rights Directive.
            </span>
          </label>
          {errors.termsAccepted && (
            <p className="auth-error">{errors.termsAccepted}</p>
          )}
        </div>

        <Button type="submit" fullWidth size="lg">Continue →</Button>
      </form>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Register panel — Step 2 (create account)
// ─────────────────────────────────────────────────────────────────────────────

function RegisterStep2({ formData, onBack, onSwitchToSignin }) {
  const { signUp } = useAuth()

  const [loading,     setLoading    ] = useState(false)
  const [serverError, setServerError] = useState('')

  async function handleCreateAccount(e) {
    e.preventDefault()
    setServerError('')
    setLoading(true)

    try {
      const { error } = await signUp({
        email:    formData.email,
        password: formData.password,
        fullName: formData.fullName,
        metadata: {
          country:           formData.country,
          native_language:   formData.nativeLanguage,
          situation:         formData.situation,
          why_here:          formData.whyHere || null,
          terms_accepted_at: formData.termsAcceptedAt,
          terms_version:     formData.termsVersion || '2026-05',
        },
      })

      if (error) throw error

      // Email confirmation enabled — user needs to confirm before signing in
      setServerError('CHECK_EMAIL')
      setLoading(false)

    } catch (err) {
      setServerError(
        isDuplicateEmailError(err?.message)
          ? 'DUPLICATE'
          : err?.message || 'Something went wrong. Please try again.'
      )
      setLoading(false)
    }
  }

  if (serverError === 'CHECK_EMAIL') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', textAlign: 'center', padding: '8px 0' }}>
        <div style={{
          width: 50, height: 50, borderRadius: '50%',
          background: 'rgba(0,201,167,.1)', border: '1px solid rgba(0,201,167,.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="#00c9a7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <h1 className="auth-title">Check your email.</h1>
        <p className="auth-subtitle" style={{ marginTop: 0 }}>
          We sent a confirmation link to{' '}
          <strong style={{ color: 'var(--text)' }}>{formData.email}</strong>.
          Click it to activate your account, then come back to sign in.
          Check your spam if you don't see it within a few minutes.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div>
        <h1 className="auth-title">Almost there.</h1>
        <p className="auth-subtitle">Step 2 of 2 — Confirm your account</p>
      </div>

      <div style={{
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: '14px 16px', fontSize: '0.85rem',
        color: 'var(--text-2)', lineHeight: 1.6,
      }}>
        Creating account for <strong style={{ color: 'var(--text)' }}>{formData.email}</strong>.
        You'll receive a confirmation email before you can sign in.
      </div>

      {serverError === 'DUPLICATE' && (
        <div className="auth-error" role="alert">
          This email is already registered.{' '}
          <button
            onClick={onSwitchToSignin}
            style={{ background: 'none', border: 'none', color: 'var(--accent)', fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
          >
            Sign in instead →
          </button>
        </div>
      )}
      {serverError && serverError !== 'DUPLICATE' && serverError !== 'CHECK_EMAIL' && (
        <div className="auth-error" role="alert">{serverError}</div>
      )}

      <form onSubmit={handleCreateAccount} noValidate>
        <div style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
          color: 'var(--text-3)', lineHeight: 1.55,
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '8px', padding: '10px 12px',
          marginBottom: '14px',
        }}>
          By creating an account, you agree to the{' '}
          <a href="https://techbench.it/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>Terms &amp; Conditions</a>,{' '}
          <a href="https://techbench.it/user-agreement" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>User Agreement</a>, and{' '}
          <a href="https://techbench.it/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>Privacy Policy</a>. You confirm you are at least 18 years old.
        </div>
        <Button type="submit" fullWidth loading={loading} size="lg">
          Create account
        </Button>
      </form>

      <p className="auth-switch" style={{ marginTop: -4 }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', color: 'var(--text-3)',
          fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'var(--font-sans)',
          textDecoration: 'underline',
        }}>
          &larr; Back
        </button>
      </p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export — tabbed Login/Register page
// ─────────────────────────────────────────────────────────────────────────────

export function Login() {
  const { isAuthenticated, onboardingComplete, loading } = useAuth()
  const navigate       = useNavigate()
  const location       = useLocation()
  const [searchParams] = useSearchParams()
  const initialTab     = location.state?.tab || searchParams.get('tab') || 'signin'

  // Already authenticated — redirect immediately
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(onboardingComplete ? '/dashboard' : '/onboarding', { replace: true })
    }
  }, [isAuthenticated, onboardingComplete, loading, navigate])

  const [tab, setTab]         = useState(initialTab)
  const [regStep, setRegStep] = useState(1)
  const [regData, setRegData] = useState(null)

  function handleTabChange(t) {
    setTab(t)
    setRegStep(1)
    setRegData(null)
  }

  function handleRegNext(data) {
    setRegData(data)
    setRegStep(2)
  }

  // On step 2 "back" — go to step 1, not back to sign-in tab
  function handleRegBack() {
    setRegStep(1)
  }

  // When "Sign in instead →" is clicked from duplicate email error
  function handleSwitchToSignin() {
    setTab('signin')
    setRegStep(1)
    setRegData(null)
  }

  const isWide = tab === 'register'

  return (
    <div className="auth-shell">
      <div className={`auth-card${isWide ? ' auth-card--wide' : ''}`}>

        <a href={process.env.REACT_APP_SITE_URL || 'https://techbench.it'}
          className="auth-logo" style={{ textDecoration: 'none' }}>
          <div className="auth-logo__mark">{LOGO}</div>
          <span className="auth-logo__text">TechBench</span>
        </a>

        {/* Only show tabs when not on register step 2 */}
        {!(tab === 'register' && regStep === 2) && (
          <Tabs active={tab} onChange={handleTabChange} />
        )}

        {tab === 'signin' && (
          <SignInPanel onSwitch={() => handleTabChange('register')} />
        )}

        {tab === 'register' && regStep === 1 && (
          <RegisterStep1 onNext={handleRegNext} />
        )}

        {tab === 'register' && regStep === 2 && (
          <RegisterStep2
            formData={regData}
            onBack={handleRegBack}
            onSwitchToSignin={handleSwitchToSignin}
          />
        )}

        <p className="auth-switch">
          <a href={process.env.REACT_APP_SITE_URL || 'https://techbench.it'}
            style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>
            &larr; Back to home
          </a>
        </p>

      </div>
    </div>
  )
}
